<template>
    <InstituteLayout>
      <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div class="container mx-auto max-w-6xl">
          <div class="bg-blue-500 text-white p-6 rounded-t-2xl px-8 py-4">
            <h1 class="text-2xl font-semibold">Latest News Dashboard</h1>
          </div>
          <div class="bg-white p-6 space-y-8">
            <div class="grid md:grid-cols-2 gap-8">
              <div>
                <div class="flex items-center gap-2 mb-4">
                  <ImageIcon class="w-5 h-5 text-gray-600" />
                  <h2 class="text-lg font-medium text-gray-800">Add News Event</h2>
                </div>
                <div class="space-y-4">
                  Upload Image
                  <div class="relative mt-2">
                    <input
                      ref="fileInput"
                      type="file"
                      accept="image/*"
                      @change="handleImageUpload"
                      class="hidden"
                    />
                    <div
                      @click="triggerFileUpload"
                      class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                      :class="{ 'border-blue-400 bg-blue-50': uploadedImage }"
                    >
                      <div v-if="uploadedImage" class="space-y-2">
                        <img
                          :src="uploadedImage"
                          alt="Uploaded preview"
                          class="w-20 h-20 object-cover rounded-lg mx-auto"
                        />
                        <p class="text-blue-600 font-medium">Image uploaded successfully!</p>
                        <p class="text-sm text-gray-500">Click to change image</p>
                      </div>
                      <div v-else>
                        <Upload class="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p class="text-gray-600 font-medium">Click to upload event image</p>
                        <p class="text-sm text-gray-500">PNG, JPG, SVG up to 5MB</p>
                      </div>
                    </div>
                  </div>
                   <div class="mt-1">Event Details Form</div> 
                  <div class="space-y-3">
                    <input
                      v-model="newEvent.title"
                      type="text"
                      placeholder="Event Title"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      v-model="newEvent.location"
                      type="text"
                      placeholder="Location"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                     <div class="mt-1">Date Range Section</div> 
                    <div class="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div class="flex items-center gap-2 text-blue-700 mb-2">
                        <Calendar class="w-4 h-4" />
                        <span class="text-sm font-medium">Event Duration</span>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            v-model="newEvent.startDate"
                            type="date"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            v-model="newEvent.endDate"
                            type="date"
                            :min="newEvent.startDate"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      </div>
                      <div v-if="newEvent.startDate && newEvent.endDate" class="flex items-center gap-2 mt-2">
                        <span class="text-xs text-gray-600">Auto Status:</span>
                        <span
                          :class="{
                            'bg-green-100 text-green-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'ongoing',
                            'bg-red-100 text-red-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'expired',
                            'bg-blue-100 text-blue-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'upcoming'
                          }"
                          class="px-2 py-1 rounded-full text-xs font-medium"
                        >
                          {{ calculateEventStatus(newEvent.startDate, newEvent.endDate).charAt(0).toUpperCase() + calculateEventStatus(newEvent.startDate, newEvent.endDate).slice(1) }}
                        </span>
                      </div>
                      <div v-if="newEvent.startDate && newEvent.endDate" class="text-xs text-gray-600">
                        <div v-if="calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'upcoming'">
                          Event starts in {{ getDaysUntil(newEvent.startDate) }} days
                        </div>
                        <div v-else-if="calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'ongoing'">
                          Event ends in {{ getDaysUntil(newEvent.endDate) }} days
                        </div>
                        <div v-else-if="calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'expired'">
                          Event ended {{ getDaysSince(newEvent.endDate) }} days ago
                        </div>
                      </div>
                    </div>
                    <textarea
                      v-model="newEvent.description"
                      placeholder="Event Description"
                      rows="3"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div>
                <div class="flex items-center gap-2 mb-4">
                  <Settings class="w-5 h-5 text-gray-600" />
                  <h2 class="text-lg font-medium text-gray-800">Quick Actions</h2>
                </div>
                <div class="space-y-3">
                  <button
                    @click="addEvent"
                    :disabled="!canAddEvent"
                    :class="canAddEvent ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
                    class="w-full text-white rounded-lg p-4 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus class="w-5 h-5" />
                    Add News Event
                  </button>
                  <button
                    @click="clearForm"
                    class="w-full bg-gray-100 border border-gray-300 rounded-lg p-4 text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <RotateCcw class="w-5 h-5" />
                    Clear Form
                  </button>
                   Quick Date Presets 
                  <div class="space-y-2">
                    <p class="text-sm font-medium text-gray-700">Quick Date Presets:</p>
                    <div class="grid grid-cols-2 gap-2">
                      <button
                        @click="setDatePreset('today')"
                        class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
                      >
                        Today Only
                      </button>
                      <button
                        @click="setDatePreset('week')"
                        class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
                      >
                        This Week
                      </button>
                      <button
                        @click="setDatePreset('month')"
                        class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
                      >
                        This Month
                      </button>
                      <button
                        @click="setDatePreset('quarter')"
                        class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
                      >
                        3 Months
                      </button>
                    </div>
                  </div>
                   Additional Actions 
                  <div class="pt-4 border-t border-gray-200 space-y-2">
                    <button
                      @click="exportEvents"
                      class="w-full bg-green-100 border border-green-300 rounded-lg p-3 text-green-700 hover:bg-green-50 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Download class="w-4 h-4" />
                      Export Events
                    </button>
                    <button
                      @click="importEvents"
                      class="w-full bg-purple-100 border border-purple-300 rounded-lg p-3 text-purple-700 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Upload class="w-4 h-4" />
                      Import Events
                    </button>
                  </div>
                </div>
              </div>
            </div>
             Design Customization 
            <div>
              <div class="flex items-center gap-2 mb-6">
                <Palette class="w-5 h-5 text-blue-500" />
                <h2 class="text-lg font-medium text-gray-800">Design Customization</h2>
              </div>
              <div class="grid md:grid-cols-4 gap-6">
                 Card Background 
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Card Background</label>
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 border border-gray-300 rounded" :style="{ backgroundColor: settings.cardBackground }"></div>
                    <input
                      v-model="settings.cardBackground"
                      type="color"
                      class="w-16 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                    <span class="text-sm text-gray-600">{{ settings.cardBackground }}</span>
                  </div>
                </div>
                 Text Color 
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 border border-gray-300 rounded" :style="{ backgroundColor: settings.textColor }"></div>
                    <input
                      v-model="settings.textColor"
                      type="color"
                      class="w-16 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                    <span class="text-sm text-gray-600">{{ settings.textColor }}</span>
                  </div>
                </div>
                 Font Family 
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    v-model="settings.fontFamily"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sans-serif">Sans-serif</option>
                    <option value="serif">Serif</option>
                    <option value="monospace">Monospace</option>
                  </select>
                </div>
                 Card Layout 
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Card Layout</label>
                  <div class="space-y-2">
                    <label class="flex items-center">
                      <input
                        v-model="settings.layout"
                        type="radio"
                        value="grid"
                        class="mr-2 text-blue-500"
                      />
                      <span class="text-sm">Grid</span>
                    </label>
                    <label class="flex items-center">
                      <input
                        v-model="settings.layout"
                        type="radio"
                        value="list"
                        class="mr-2 text-blue-500"
                      />
                      <span class="text-sm">List</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-end">
              <button
                @click="saveChanges"
                class="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                <Save class="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
          <div class="h-8"></div>
          <div class="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div class="bg-blue-500 text-white p-4">
              <div class="flex items-center gap-2">
                <Eye class="w-5 h-5" />
                <h2 class="text-lg font-medium">Live Preview</h2>
              </div>
            </div>
            <div class="p-6">
              <div :class="settings.layout === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'">
                <div
                  v-if="newEvent.title || newEvent.location || newEvent.description || uploadedImage"
                  class="rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105 border-blue-300"
                  :style="{
                    backgroundColor: settings.cardBackground,
                    color: settings.textColor,
                    fontFamily: settings.fontFamily
                  }"
                >
                  <div class="h-48 bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center relative">
                    <img
                      v-if="uploadedImage"
                      :src="uploadedImage"
                      alt="Event preview"
                      class="w-full h-full object-cover"
                    />
                    <ImageIcon v-else class="w-12 h-12 text-white opacity-75" />
                  </div>
                  <div class="p-4">
                    <div class="flex items-center justify-between mb-2">
                      <span
                        v-if="newEvent.startDate && newEvent.endDate"
                        :class="{
                          'bg-green-100 text-green-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'ongoing',
                          'bg-red-100 text-red-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'expired',
                          'bg-blue-100 text-blue-800': calculateEventStatus(newEvent.startDate, newEvent.endDate) === 'upcoming'
                        }"
                        class="px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {{ calculateEventStatus(newEvent.startDate, newEvent.endDate).charAt(0).toUpperCase() + calculateEventStatus(newEvent.startDate, newEvent.endDate).slice(1) }}
                      </span>
                      <span v-else class="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Set Dates
                      </span>
                    </div>
                    <h3 class="font-semibold text-lg mb-2">{{ newEvent.title || 'Event Title' }}</h3>
                    <div class="flex items-center gap-2 text-sm opacity-75 mb-2" v-if="newEvent.startDate && newEvent.endDate">
                      <Calendar class="w-4 h-4" />
                      <span>{{ formatDate(newEvent.startDate) }} - {{ formatDate(newEvent.endDate) }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-sm opacity-75 mb-3" v-if="newEvent.location">
                      <MapPin class="w-4 h-4" />
                      <span>{{ newEvent.location }}</span>
                    </div>
                    <p class="text-sm opacity-90 line-clamp-3">{{ newEvent.description || 'Event description will appear here...' }}</p>
                  </div>
                </div>
              </div>
              <div class="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div class="flex items-center gap-2 text-blue-700">
                  <Info class="w-4 h-4" />
                  <span class="text-sm font-medium">Auto Status Information</span>
                </div>
                <p class="text-sm text-blue-500 mt-2">
                  Event status is automatically calculated based on the current date:
                </p>
                <ul class="text-xs text-blue-600 mt-2 space-y-1">
                  <li>• <strong>Upcoming:</strong> Current date is before start date</li>
                  <li>• <strong>Ongoing:</strong> Current date is between start and end date</li>
                  <li>• <strong>Expired:</strong> Current date is after end date</li>
                </ul>
              </div>
              <div class="flex justify-end mt-4">
                <button
                  @click="handleDeleteCurrentDraft"
                  class="px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center gap-2"
                >
                  <Trash2 class="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </InstituteLayout>
  </template>
  
  <script setup>
  import InstituteLayout from "../institutelayout.vue"
  import { ref, reactive, computed } from 'vue'
  import {
    ImageIcon,
    Upload,
    Settings,
    Plus,
    Palette,
    Calendar,
    MapPin,
    Save,
    Eye,
    Info,
    RotateCcw,
    Download,
    Trash2
  } from 'lucide-vue-next'
  
  // Sample events for preview with date ranges
  const sampleEvents = ref([
    {
      title: "Tech Conference 2024",
      startDate: "2024-03-15",
      endDate: "2024-03-17",
      location: "San Francisco, CA",
      description: "Join us for the biggest tech conference of the year featuring keynotes from industry leaders, hands-on workshops, and networking opportunities.",
      image: "/placeholder.svg?height=192&width=400", // Added placeholder image
    },
    {
      title: "Art Exhibition Opening",
      startDate: "2024-02-28",
      endDate: "2024-03-05",
      location: "New York, NY",
      description: "Experience contemporary art from emerging artists in this exclusive gallery opening featuring interactive installations.",
      image: "/placeholder.svg?height=192&width=400", // Added placeholder image
    },
    {
      title: "Music Festival",
      startDate: "2024-04-20",
      endDate: "2024-04-22",
      location: "Austin, TX",
      description: "Three days of incredible music featuring top artists from around the world, food trucks, and camping options available.",
      image: "/placeholder.svg?height=192&width=400", // Added placeholder image
    }
  ])
  
  const newEvent = reactive({
    title: '',
    startDate: '',
    endDate: '',
    location: '',
    description: ''
  })
  
  const settings = reactive({
    cardBackground: '#ffffff',
    textColor: '#374151',
    fontFamily: 'sans-serif',
    layout: 'grid'
  })
  
  // File upload refs
  const fileInput = ref(null)
  const uploadedImage = ref(null)
  
  // Computed property to check if event can be added
  const canAddEvent = computed(() => {
    return newEvent.title && newEvent.startDate && newEvent.endDate && newEvent.location
  })
  
  // Image upload methods
  const triggerFileUpload = () => {
    fileInput.value?.click()
  }
  
  const handleImageUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB')
        return
      }
  
      const reader = new FileReader()
      reader.onload = (e) => {
        uploadedImage.value = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }
  
  // Export/Import methods
  const exportEvents = () => {
    const eventsData = {
      events: sampleEvents.value,
      settings: settings,
      exportDate: new Date().toISOString()
    }
  
    const dataStr = JSON.stringify(eventsData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
  
    const link = document.createElement('a')
    link.href = URL.createObjectURL(dataBlob)
    link.download = `news-events-${new Date().toISOString().split('T')[0]}.json`
    link.click()
  
    alert('Events exported successfully!')
  }
  
  const importEvents = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
  
    input.onchange = (event) => {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const importedData = JSON.parse(e.target.result)
            if (importedData.events) {
              sampleEvents.value = importedData.events
            }
            if (importedData.settings) {
              Object.assign(settings, importedData.settings)
            }
            alert('Events imported successfully!')
          } catch (error) {
            alert('Error importing file. Please check the file format.')
          }
        }
        reader.readAsText(file)
      }
    }
  
    input.click()
  }
  
  // Existing methods
  const calculateEventStatus = (startDate, endDate) => {
    const today = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)
  
    today.setHours(0, 0, 0, 0)
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
  
    if (today < start) {
      return 'upcoming'
    } else if (today >= start && today <= end) {
      return 'ongoing'
    } else {
      return 'expired'
    }
  }
  
  const getDaysUntil = (date) => {
    const today = new Date()
    const targetDate = new Date(date)
    const diffTime = targetDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }
  
  const getDaysSince = (date) => {
    const today = new Date()
    const targetDate = new Date(date)
    const diffTime = today - targetDate
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }
  
  const setDatePreset = (preset) => {
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
  
    switch (preset) {
      case 'today':
        newEvent.startDate = todayStr
        newEvent.endDate = todayStr
        break
      case 'week':
        newEvent.startDate = todayStr
        const weekEnd = new Date(today)
        weekEnd.setDate(today.getDate() + 6)
        newEvent.endDate = weekEnd.toISOString().split('T')[0]
        break
      case 'month':
        newEvent.startDate = todayStr
        const monthEnd = new Date(today)
        monthEnd.setMonth(today.getMonth() + 1)
        monthEnd.setDate(monthEnd.getDate() - 1)
        newEvent.endDate = monthEnd.toISOString().split('T')[0]
        break
      case 'quarter':
        newEvent.startDate = todayStr
        const quarterEnd = new Date(today)
        quarterEnd.setMonth(today.getMonth() + 3)
        quarterEnd.setDate(quarterEnd.getDate() - 1)
        newEvent.endDate = quarterEnd.toISOString().split('T')[0]
        break
    }
  }
  
  const addEvent = () => {
    if (canAddEvent.value) {
      const status = calculateEventStatus(newEvent.startDate, newEvent.endDate)
  
      // Add the new event to sample events
      const newEventData = {
        title: newEvent.title,
        startDate: newEvent.startDate,
        endDate: newEvent.endDate,
        location: newEvent.location,
        description: newEvent.description,
        image: uploadedImage.value // Include the uploaded image
      }
  
      sampleEvents.value.push(newEventData)
  
      alert(`Event "${newEvent.title}" added successfully with status: ${status}!`)
      clearForm()
    } else {
      alert('Please fill in all required fields (Title, Start Date, End Date, Location)')
    }
  }
  
  const clearForm = () => {
    Object.assign(newEvent, {
      title: '',
      startDate: '',
      endDate: '',
      location: '',
      description: ''
    })
    uploadedImage.value = null
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
  
  const handleDeleteCurrentDraft = () => {
    clearForm()
    alert('Current event draft has been cleared.')
  }
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }
  
  const saveChanges = () => {
    // Save to localStorage for persistence
    const dataToSave = {
      events: sampleEvents.value,
      settings: settings
    }
    localStorage.setItem('newsEventsData', JSON.stringify(dataToSave))
    alert('Design settings and events saved successfully!')
  }
  
  // Load saved data on component mount
  const loadSavedData = () => {
    const savedData = localStorage.getItem('newsEventsData')
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        if (parsedData.events) {
          sampleEvents.value = parsedData.events
        }
        if (parsedData.settings) {
          Object.assign(settings, parsedData.settings)
        }
      } catch (error) {
        console.error('Error loading saved data:', error)
      }
    }
  }
  
  // Load data when component mounts
  loadSavedData()
  </script>
  
  <style scoped>
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  </style>
  